import { FC, useState } from 'react'
import { LoadingAndErrors } from '../../../../components/custom_components/LoadingAndErrors'
import { useScoreGrid } from '../../../../graphql/score-grid/hooks/useScoreGrid'
import { FullMeetingFragment, MeetingStepStatus, MeetingStepViewId, ScoreGridDto, Country } from '../../../../types'
import { AppraisalContextType } from '../../AppraisalTypes'
import '../../evaluation/evaluation.less'
import EvaluationContentContainer from '../../evaluation/EvaluationContentContainer'
import { EvaluationFooterComponent } from '../../evaluation/EvaluationFooterComponent'
import { EvaluationStepButtonsComponent } from '../../evaluation/EvaluationStepButtonsComponent'
import { AppraisalStepBtns } from '../shared/appraisal-types'
import { AppraisalFlowHelper } from '../shared/AppraisalFlowHelper'
import { generateInitialState, allScoreGoalsExist } from '../../evaluation/EvaluationUtil'

interface Props extends AppraisalStepBtns {
  meeting: FullMeetingFragment
}

interface ManagerMeetingStepEvaluationContentProps extends Props {
  currentGrid: ScoreGridDto
  isIdVWeightEnabled: boolean
}

const ManagerMeetingStepEvaluation: FC<Props> = ({ meeting, backBtn, nextBtn }) => {
  const { data, loading, error } = useScoreGrid(meeting.id, meeting.employee.id, meeting.appraisalSeason.country as Country, true)

  if (loading || error || !data || !data.scoreGridWithHistory) {
    return <LoadingAndErrors isLoading={loading} hasNoData={!data} error={error} />
  }

  const currentGrid = { ...data.scoreGridWithHistory }
  const isIdVWeightEnabled = data.isIdVWeightEnabled

  return (
    <ManagerMeetingStepEvaluationContent
      isIdVWeightEnabled={isIdVWeightEnabled}
      currentGrid={currentGrid}
      meeting={meeting}
      backBtn={backBtn}
      nextBtn={nextBtn}
    />
  )
}

const ManagerMeetingStepEvaluationContent: FC<ManagerMeetingStepEvaluationContentProps> = ({
  meeting,
  backBtn,
  nextBtn,
  currentGrid,
  isIdVWeightEnabled,
}) => {
  const [evaluationStepStatus, setEvaluationStepStatus] = useState(MeetingStepStatus.Visited)
  const [goalsState, setGoalsState] = useState(generateInitialState(currentGrid.scoreGoals, AppraisalContextType.Manager))
  const employeeName = meeting.employee.chosenFirstName ? meeting.employee.chosenFirstName : meeting.employee.legalFirstName
  const hasValidEmployeeScore = !!currentGrid.employeePreparationScore && meeting.appraisal!.evaluationShared
  const showFooter =
    hasValidEmployeeScore ||
    !!currentGrid.lastResultScore ||
    (isIdVWeightEnabled && (!!currentGrid.managerPreparationScore || allScoreGoalsExist(goalsState, AppraisalContextType.Manager)))

  return (
    <div data-e2e-manager-meeting-step-evaluation>
      <EvaluationContentContainer
        goalsState={goalsState}
        setGoalsState={setGoalsState}
        grid={currentGrid}
        meeting={meeting}
        isManagerEvaluation={true}
        stepStatus={evaluationStepStatus}
        type={AppraisalContextType.Manager}
        onStatusChange={setEvaluationStepStatus}
        showFooter={showFooter}
      />

      <EvaluationFooterComponent
        meeting={meeting}
        isIdVWeightEnabled={isIdVWeightEnabled}
        goalsState={goalsState}
        currentGrid={currentGrid}
        type={AppraisalContextType.Manager}
        evaluationScore={currentGrid.managerPreparationScore}
        employeeName={employeeName}
        employeePreparationPublic={meeting.appraisal!.evaluationShared}
        showFooter={showFooter}
      />

      <EvaluationStepButtonsComponent meetingId={meeting.id} backBtn={backBtn} nextBtn={nextBtn} currentStepStatus={evaluationStepStatus} />
      <AppraisalFlowHelper stepViewId={MeetingStepViewId.Evaluation} stepStatus={evaluationStepStatus} />
    </div>
  )
}

export default ManagerMeetingStepEvaluation
